import { type ReactNode } from 'react'
import { isNotEmpty, type CardLink } from '@terros/common'
import { Box, Center, Group, Image, Input, Paper, SimpleGrid, Stack, Text, TextInput } from '@mantine/core'
import { useTranslation } from '@front/model'
import { ButtonAdd, ButtonDelete, ButtonExternal, MaterialSymbolIcon, TCard } from '@components'
import { type CompanyCardFieldsProps } from './CompanyCardFields'
import { AppearanceFields } from './AppearanceFields'

export const DEFAULT_COMPANY_LINKS: CardLink[] = [
  { label: 'Facebook', url: '' },
  { label: 'LinkedIn', url: '' },
  { label: 'Instagram', url: '' },
]

export const BrandingFields = ({ company, form }: CompanyCardFieldsProps): ReactNode => {
  const { t } = useTranslation()
  const links = form.values.links ?? []

  return (
    <Stack gap='sm'>
      <TCard>
        <Stack gap='lg'>
          <TCard.Header>
            <TCard.Title>{t('Branding')}</TCard.Title>
          </TCard.Header>
          <SimpleGrid cols={2} spacing='xl'>
            <Input.Wrapper label={t('Brand banner')} labelElement='div'>
              <Stack gap='xs' mt='xs'>
                <Paper bg='var(--mantine-color-body)' p='sm' radius='md' withBorder>
                  <Stack align='center' gap='xs' justify='center'>
                    <Box h={112} w='100%'>
                      {isNotEmpty(company.avatarUrl) ? (
                        <Image src={company.avatarUrl} alt={company.name} fit='contain' h='100%' w='100%' />
                      ) : (
                        <Center h='100%'>
                          <MaterialSymbolIcon icon='storefront' />
                        </Center>
                      )}
                    </Box>
                    <Text fw='bold' size='sm' ta='center'>
                      {company.name}
                    </Text>
                  </Stack>
                </Paper>
                <ButtonExternal to='/settings/company' size='xs' variant='ghost'>
                  {t('Change in Settings')}
                </ButtonExternal>
              </Stack>
            </Input.Wrapper>
            <AppearanceFields form={form} />
          </SimpleGrid>
        </Stack>
      </TCard>

      <TCard>
        <Stack>
          <TCard.Header>
            <TCard.Title>{t('Card URL')}</TCard.Title>
          </TCard.Header>
          <TextInput
            required
            leftSection={<Text size='xs'>card.terros.com/</Text>}
            leftSectionWidth={130}
            {...form.getInputProps('slug')}
          />
        </Stack>
      </TCard>

      <TCard>
        <Stack gap='lg'>
          <TCard.Header>
            <TCard.Title>{t('Company links')}</TCard.Title>
            <Input.Description>{t('Social links to show on the card.')}</Input.Description>
          </TCard.Header>
          <Stack gap='sm'>
            {links.map((link, index) => (
              <Group key={form.key(`links.${index}`)} align='flex-end' wrap='nowrap'>
                <TextInput
                  aria-label={t('Label')}
                  flex={1}
                  label={index === 0 ? t('Label') : undefined}
                  placeholder={t('New link')}
                  value={link.label ?? ''}
                  onChange={(event) => {
                    const label = event.currentTarget.value
                    form.setFieldValue(`links.${index}.label`, isNotEmpty(label) ? label : undefined)
                  }}
                />
                <TextInput
                  aria-label={t('URL')}
                  flex={2}
                  label={index === 0 ? t('URL') : undefined}
                  leftSection={
                    <Box aria-hidden='true' display='flex'>
                      <MaterialSymbolIcon icon='public' size='md' />
                    </Box>
                  }
                  placeholder={t('Paste a URL')}
                  {...form.getInputProps(`links.${index}.url`)}
                />
                <ButtonDelete
                  buttonType='icon-rounded'
                  size='small'
                  tooltip={t('Remove link')}
                  onClick={() => form.removeListItem('links', index)}
                />
              </Group>
            ))}
          </Stack>
          <ButtonAdd variant='secondary' onClick={() => form.insertListItem('links', { url: '' })}>
            {t('Add link')}
          </ButtonAdd>
        </Stack>
      </TCard>
    </Stack>
  )
}

export function toSuggestedSlug(companyName: string): string {
  return companyName
    .toLowerCase()
    .replace(/[^a-z0-9._~-]+/g, '-')
    .replace(/^-|-$/g, '')
}

export function withDefaultCompanyLinks(links: CardLink[], includeMissingDefaults: boolean): CardLink[] {
  const matchedLinks = new Set<CardLink>()
  const defaultLinks: CardLink[] = []
  for (const defaultLink of DEFAULT_COMPANY_LINKS) {
    const existingLink = links.find((link) => !matchedLinks.has(link) && matchesDefaultCompanyLink(link, defaultLink))
    if (existingLink) {
      matchedLinks.add(existingLink)
      defaultLinks.push({ ...existingLink })
    } else if (includeMissingDefaults) {
      defaultLinks.push({ ...defaultLink })
    }
  }
  const customLinks = links.filter((link) => !matchedLinks.has(link))
  return [...defaultLinks, ...customLinks]
}

export function isDefaultCompanyLink(link: CardLink): boolean {
  return DEFAULT_COMPANY_LINKS.some((defaultLink) => matchesDefaultCompanyLink(link, defaultLink))
}

function matchesDefaultCompanyLink(link: CardLink, defaultLink: CardLink): boolean {
  return link.label?.trim().toLowerCase() === defaultLink.label?.trim().toLowerCase()
}
